// apps/web/src/lib/painel/home/ao-vivo.ts — Fase 17.8, Plano 12 (D-13; UI-SPEC U-25).
// Decisões do canal ao vivo em funções puras (sem DOM): leitura segura da mensagem do stream, máquina de estados da conexão e o
// texto do indicador "Atualizado há Xs" da topbar. O provider e o hook só ligam estas funções ao navegador.
import { ID_PERFIS_HOME, type PainelEventoStream, type PerfilHome } from '@pieltec/contracts';
import { tempoRelativo } from './formato';

/** Espera depois de um evento para recarregar a home (o servidor já coalesce rajadas em 1 s). */
export const DEBOUNCE_REFETCH_MS = 5000;
/** Recarga de segurança: a cada ~2 min, só com a aba em primeiro plano. */
export const RECARGA_VISIVEL_MS = 120_000;
/** Sem reabrir a conexão por este tempo, o indicador passa a "Reconectando…". */
export const LIMITE_RECONECTANDO_MS = 10_000;
/** Sem nenhum dado por este tempo, o indicador passa a "Sem atualização há {n} min". */
export const LIMITE_SEM_ATUALIZACAO_MS = 300_000;
/** Intervalo em que o indicador recalcula o texto relativo. */
export const INTERVALO_INDICADOR_MS = 10_000;

export type EstadoAoVivo = 'conectando' | 'conectado' | 'reconectando' | 'sem-atualizacao' | 'nao-autorizado';

export type SinalAoVivo = 'aberto' | 'erro' | 'ping' | 'dados' | 'tick' | 'nao-autorizado';

const PERFIS_VALIDOS: ReadonlySet<string> = new Set<string>(ID_PERFIS_HOME);

/**
 * Lê uma mensagem do stream com allowlist de `tipo` e de perfis (T-17.8-12-01). JSON inválido, tipo desconhecido ou campos de
 * outro formato viram `null` e a mensagem é ignorada; perfis fora da lista conhecida são descartados.
 */
export function interpretarEventoPainel(texto: string): PainelEventoStream | null {
  let bruto: unknown;
  try {
    bruto = JSON.parse(texto);
  } catch {
    return null;
  }
  if (typeof bruto !== 'object' || bruto === null) return null;
  const obj = bruto as Record<string, unknown>;
  if (obj.tipo === 'ping') {
    return typeof obj.timestamp === 'string' ? { tipo: 'ping', timestamp: obj.timestamp } : null;
  }
  if (obj.tipo === 'painel_invalidar') {
    if (!Array.isArray(obj.perfis) || typeof obj.badges !== 'boolean' || typeof obj.timestamp !== 'string') return null;
    const perfis = obj.perfis.filter((p): p is PerfilHome => typeof p === 'string' && PERFIS_VALIDOS.has(p));
    return { tipo: 'painel_invalidar', perfis, badges: obj.badges, timestamp: obj.timestamp };
  }
  return null;
}

/**
 * Próximo estado da conexão. `desdeErro` é o tempo desde o primeiro erro da queda atual (0 quando conectada) e `desdeDados` o
 * tempo desde a última abertura, ping ou evento. `nao-autorizado` (401/403 na sondagem) é terminal.
 */
export function proximoEstadoAoVivo(
  atual: EstadoAoVivo,
  sinal: SinalAoVivo,
  ms: { readonly desdeErro: number; readonly desdeDados: number },
): EstadoAoVivo {
  if (atual === 'nao-autorizado' || sinal === 'nao-autorizado') return 'nao-autorizado';
  if (sinal === 'aberto' || sinal === 'ping' || sinal === 'dados') return 'conectado';
  // erro e tick: a queda só aparece depois de 10 s sem reabrir; 5 min sem dado nenhum vira "sem atualização".
  if (ms.desdeDados >= LIMITE_SEM_ATUALIZACAO_MS) return 'sem-atualizacao';
  if (ms.desdeErro >= LIMITE_RECONECTANDO_MS) return 'reconectando';
  return atual;
}

/** Estados em que a pessoa precisa saber que o dado pode estar velho (anunciados uma vez ao leitor de tela). */
export function estadoDeAtencao(estado: EstadoAoVivo): boolean {
  return estado === 'reconectando' || estado === 'sem-atualizacao' || estado === 'nao-autorizado';
}

/** Texto do indicador da topbar: 'Atualizado agora', 'Atualizado há 12 s', 'Reconectando…', 'Sem atualização há 6 min'. */
export function textoIndicador(estado: EstadoAoVivo, ultimaAtualizacao: Date | null, agora: Date): string {
  if (estado === 'reconectando') return 'Reconectando…';
  if (estado === 'nao-autorizado') return 'Sem atualização ao vivo';
  if (ultimaAtualizacao === null) return estado === 'conectando' ? 'Conectando…' : 'Aguardando dados';
  if (estado === 'sem-atualizacao') {
    const minutos = Math.max(5, Math.floor((agora.getTime() - ultimaAtualizacao.getTime()) / 60_000));
    return `Sem atualização há ${minutos} min`;
  }
  return `Atualizado ${tempoRelativo(ultimaAtualizacao, agora)}`;
}
